/**
 * Student auth — JWT-based login/signup for the public side of SuperTmatik.
 *
 * Token persistence: localStorage (key `supert_student_token`) so login
 * survives across tabs and reloads. Profile cache: localStorage
 * (`supert_student_user`) — refreshed on every successful request to /me.
 *
 * Guest mode is preserved: a user without a token is still allowed to play
 * by filling in name/school via sessionStorage. When they DO log in, the
 * sessionStorage profile is overwritten by the persisted DB profile so the
 * Header/PlayerProfileCard reflect their identity automatically.
 */
import { api } from "@/lib/api";

const TOKEN_KEY = "supert_student_token";
const USER_KEY = "supert_student_user";

const listeners = new Set();

function emit() {
  for (const l of listeners) {
    try { l(getCurrentUser()); } catch { /* ignore */ }
  }
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getCurrentUser() {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Mirror the logged-in profile to sessionStorage so the game runtime
// (which still reads sessionStorage for name/school/nationality) picks up
// the persisted identity automatically.
function syncToSessionProfile(user) {
  if (!user) return;
  sessionStorage.setItem("player_name", user.display_name || "");
  sessionStorage.setItem("player_school", user.school || "");
  sessionStorage.setItem("player_nationality", user.nationality || "PT");
  // Mark identity as persisted so prompts skip the "edit profile" flow.
  sessionStorage.setItem("student_logged_in", "1");
}

function persist(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  syncToSessionProfile(user);
  emit();
}

export async function register({ email, password, display_name, school, nationality }) {
  const { data } = await api.post("/auth/student/register", {
    email, password, display_name, school: school || "", nationality: nationality || "PT",
  });
  persist(data.token, data.user);
  return data.user;
}

export async function login({ email, password }) {
  const { data } = await api.post("/auth/student/login", { email, password });
  persist(data.token, data.user);
  return data.user;
}

export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  sessionStorage.removeItem("student_logged_in");
  emit();
}

// Re-validate the token on app boot. Returns the user (or null on failure).
export async function refreshFromServer() {
  const token = getToken();
  if (!token) return null;
  try {
    const { data } = await api.get("/auth/student/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    localStorage.setItem(USER_KEY, JSON.stringify(data));
    syncToSessionProfile(data);
    emit();
    return data;
  } catch {
    logout();
    return null;
  }
}
